// src/pages/PrivacyPolicy.js

import React, { useEffect } from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FaArrowLeft, FaShieldAlt, FaLock, FaEye, FaCookieBite } from "react-icons/fa";
import { useTheme } from "../context/ThemeContext";

const PrivacyPolicy = () => {
  const { isDarkMode } = useTheme();
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.style.overflow = 'auto';
  }, []);

  const headingStyle = {
    color: isDarkMode ? '#60a5fa' : '#2563eb',
    fontSize: '1.5rem',
    fontWeight: 'bold',
    marginBottom: '20px'
  };

  const subHeadingStyle = {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    marginBottom: '10px',
    display: 'flex',
    alignItems: 'center'
  };

  const sectionStyle = { marginBottom: '40px' };
  const paraStyle = { fontSize: '1rem', lineHeight: '1.7', marginBottom: '15px' };
  const listStyle = { paddingLeft: '40px' };
  const listItemStyle = { marginBottom: '8px' };

  const infoBoxStyle = {
    backgroundColor: isDarkMode ? '#334155' : '#f8fafc',
    padding: '20px',
    borderRadius: '10px'
  };

  const checkItem = (text) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
      <span style={{ color: '#10b981', marginRight: '15px', fontSize: '1.2rem', flexShrink: 0 }}>✓</span>
      <span style={{ fontSize: '1rem', lineHeight: '1.6' }}>{text}</span>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      style={{
        backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
        minHeight: '100vh',
        color: isDarkMode ? '#e2e8f0' : '#1e293b'
      }}
    >
      {/* Header */}
      <div style={{
        background: "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
        padding: '40px 0',
        color: 'white'
      }}>
        <Container>
          <Row className="align-items-center">
            <Col>
              <Link to="/" style={{ color: 'white', textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <FaArrowLeft style={{ marginRight: '8px' }} />
                Back to Portfolio
              </Link>
            </Col>
            <Col className="text-end">
              <h1 style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '8px' }}>
                <FaShieldAlt style={{ marginRight: '15px' }} />
                Privacy Policy
              </h1>
              <p style={{ fontSize: '1.1rem', opacity: 0.9 }}>
                Last Updated: {currentDate}
              </p>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Main Content */}
      <Container style={{ padding: '40px 20px' }}>
        <Card style={{
          border: 'none',
          boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
          backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
          color: isDarkMode ? '#e2e8f0' : '#1e293b',
          display: 'block',
          visibility: 'visible',
          opacity: 1
        }}>
          <Card.Body style={{ padding: '40px' }}>

            {/* 1. INTRODUCTION */}
           <section style={sectionStyle}>
              <h2 style={headingStyle}>1. Introduction</h2>
              <p style={paraStyle}>
                Welcome to <strong>Shadi - The Tech Artist</strong>. This Privacy Policy explains how I collect,
                use, and protect your information when you use my website and all mobile applications published
                by me on the Google Play Store, including but not limited to <strong>VaultFinPro</strong> and future apps.
              </p>
              <p style={{ fontSize: '1rem', lineHeight: '1.7' }}>
                I respect your privacy and am committed to protecting your personal data. Please read this policy carefully.
              </p>
            </section>
            
            {/* 2. WHO I AM */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>2. Who I Am</h2>
              <div style={infoBoxStyle}>
                <p style={{ marginBottom: '5px' }}><strong>Name:</strong> Shadi Sayed Mohamed Ahmed</p>
                <p style={{ marginBottom: '5px' }}><strong>Business Name:</strong> Shadi - The Tech Artist</p>
                <p style={{ marginBottom: '5px' }}><strong>Address:</strong> 25 Wali El Ahd, Hadaek El Kobba, Cairo 11256, Egypt</p>
                <p style={{ marginBottom: '5px' }}><strong>Email:</strong> shadisayed.6890@gmail.com</p>
                <p style={{ marginBottom: '0' }}><strong>Phone:</strong> +201151638804</p>
              </div>
            </section>

            {/* 3. INFORMATION I COLLECT */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>3. Information I Collect</h2>

              <div style={{ marginBottom: '20px' }}>
                <h3 style={subHeadingStyle}>
                  <FaEye style={{ marginRight: '10px', color: isDarkMode ? '#60a5fa' : '#2563eb', fontSize: '16px' }} />
                  Information You Provide
                </h3>
                <ul style={listStyle}>
                  <li style={listItemStyle}><strong>Name and email address</strong> (when you contact me or purchase)</li>
              
                  <li style={listItemStyle}>
                    <strong>Payment information</strong> (processed securely by third-party payment providers such as
                    Creem, Gumroad, Paddle, SellMyApp, Codester, or Google Play Billing depending on the app.
                    I never store or have access to your card details)
                  </li>
                  
                  <li style={listItemStyle}><strong>Any information you include in messages</strong> to me</li>
              
                </ul>
              </div>

              <div>
                <h3 style={subHeadingStyle}>
                  <FaCookieBite style={{ marginRight: '10px', color: isDarkMode ? '#60a5fa' : '#2563eb', fontSize: '16px' }} />
                  Information Collected Automatically
                </h3>
                <ul style={listStyle}>
                  <li style={listItemStyle}><strong>Usage data</strong> (pages visited, time spent)</li>
                  <li style={listItemStyle}><strong>Device information</strong> (browser type, IP address)</li>
                  <li style={listItemStyle}><strong>Cookies and similar technologies</strong></li>
                </ul>
              </div>
            </section>

            {/* 4. HOW I USE YOUR INFORMATION */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>4. How I Use Your Information</h2>
              <div style={{ display: 'grid', gap: '4px' }}>
                {checkItem("To provide and deliver my products and services")}
                {checkItem("To communicate with you about your purchases")}
                {checkItem("To provide customer support")}
                {checkItem("To improve my website and products")}
                {checkItem("To send occasional updates (you can opt out anytime)")}
              </div>
            </section>

            {/* 5. INFORMATION SHARING */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>5. Information Sharing</h2>
              <p style={paraStyle}>
                I <strong>do not sell, trade, or rent</strong> your personal information to others.
                I only share information with:
              </p>
              <ul style={listStyle}>
                <li style={listItemStyle}>
                  <strong>Payment processors</strong> — Creem (acting as Merchant of Record, handling all
                  payment processing and global tax compliance on my behalf), Gumroad, Paddle, SellMyApp,
                  and Codester, depending on the platform used for your purchase
                </li>
                
                <li style={listItemStyle}>
                    <strong>Service providers</strong> who help operate specific applications.
                    Some apps may use cloud services such as Firebase for authentication, database, analytics, or crash reporting,
                    while other apps operate fully offline without any external services.
                </li>

                <li style={listItemStyle}>
                  <strong>When required by law</strong> to comply with legal obligations
                </li>
              </ul>
            </section>

            {/* 5.1 MOBILE APPLICATION USAGE */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>5.1 Mobile Application Usage</h2>

              <p style={paraStyle}>
                This Privacy Policy applies to all mobile applications published under my developer account on Google Play Store.
                Each application may behave differently depending on its features.
              </p>

              <ul style={listStyle}>
                <li style={listItemStyle}>
                  Some apps (such as VaultFinPro) operate fully offline and do not collect or transmit personal data.
                  All data is stored locally on the user's device.
                </li>
                <li style={listItemStyle}>
                  Some apps (such as e-commerce or cloud-based applications) may use third-party services like Firebase
                  for authentication, data storage, analytics, or crash reporting.
                </li>
                <li style={listItemStyle}>
                  Only the minimum data required for app functionality is collected in each case.
                </li>
              </ul>
            </section>

            {/* 6. DATA SECURITY */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>6. Data Security</h2>
              <div style={{
                backgroundColor: isDarkMode ? '#1e293b' : '#f1f5f9',
                padding: '20px',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: '15px'
              }}>
                <FaLock style={{ fontSize: '2rem', color: '#10b981', flexShrink: 0 }} />
                <p style={{ marginBottom: '0' }}>
                  I implement appropriate technical and organizational security measures to protect your
                  information. However, no method of transmission over the Internet is 100% secure.
                </p>
              </div>
            </section>

            {/* 7. YOUR RIGHTS */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>7. Your Rights</h2>
              <p style={paraStyle}>Depending on your location, you may have the right to:</p>
              <ul style={listStyle}>
                <li style={listItemStyle}>Access the personal information I hold about you</li>
                <li style={listItemStyle}>Request correction of inaccurate data</li>
                <li style={listItemStyle}>Request deletion of your data</li>
                <li style={listItemStyle}>Opt out of marketing communications</li>
              </ul>
              <p style={{ fontSize: '1rem', lineHeight: '1.7', marginTop: '15px' }}>
                To exercise these rights, contact me at{' '}
                <strong style={{ color: isDarkMode ? '#60a5fa' : '#2563eb' }}>shadisayed.6890@gmail.com</strong>
              </p>
            </section>

            {/* 8. COOKIES */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>8. Cookies</h2>
              <p style={paraStyle}>
                My website uses cookies to improve user experience. You can set your browser to refuse
                cookies, but some parts of the site may not function properly.
              </p>
              <div style={{
                backgroundColor: isDarkMode ? '#334155' : '#f1f5f9',
                padding: '15px',
                borderRadius: '8px',
                fontSize: '0.95rem'
              }}>
                <strong>Types of cookies used:</strong>
                <ul style={{ marginTop: '10px', marginBottom: '0' }}>
                  <li><strong>Essential cookies:</strong> Required for site operation</li>
                  <li><strong>Analytics cookies:</strong> Help me understand how visitors use the site</li>
                </ul>
              </div>
            </section>

            {/* 9. CHILDREN'S PRIVACY */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>9. Children's Privacy</h2>
              <p style={{ fontSize: '1rem', lineHeight: '1.7' }}>
                My services are not directed to individuals under 18. I do not knowingly collect information
                from children. If you are a parent and believe your child has provided information, please contact me.
              </p>
            </section>

            {/* 10. INTERNATIONAL DATA TRANSFERS */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>10. International Data Transfers</h2>

              <p style={{ fontSize: '1rem', lineHeight: '1.7' }}>
                Depending on the application you use, your information may be processed or stored in countries
                other than your own. This may include the United States or other regions where third-party
                service providers or cloud infrastructure are located.
              </p>

              <p style={{ fontSize: '1rem', lineHeight: '1.7', marginTop: '10px' }}>
                Some applications may use cloud services such as Firebase or similar platforms for hosting,
                authentication, or data storage. Other applications may operate fully offline without any
                external data transfer.
              </p>

              <p style={{ fontSize: '1rem', lineHeight: '1.7', marginTop: '10px' }}>
                By using my applications, you acknowledge that data processing location depends on the
                specific app features being used.
              </p>
            </section>
            
            {/* 11. GDPR */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>11. GDPR (For EU Users)</h2>
              <p style={paraStyle}>
                If you are in the European Economic Area (EEA), you have additional rights under the GDPR:
              </p>
              <ul style={listStyle}>
                <li style={listItemStyle}>Right to access, rectify, or erase your data</li>
                <li style={listItemStyle}>Right to data portability</li>
                <li style={listItemStyle}>Right to withdraw consent</li>
                <li style={listItemStyle}>Right to lodge a complaint with a supervisory authority</li>
              </ul>
              <p style={{ fontSize: '1rem', lineHeight: '1.7', marginTop: '15px' }}>
                My legal basis for processing your data depends on the application. It may include user consent, contract performance, or legitimate interest depending on the features used. and the performance of a contract
                (to provide products you purchased). Where Creem acts as Merchant of Record, they serve
                as the data controller for payment-related information under applicable EU law.
              </p>
            </section>

            {/* 12. CCPA */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>12. CCPA (For California Users)</h2>
              <p style={{ fontSize: '1rem', lineHeight: '1.7' }}>
                California residents have the right to request disclosure of personal information collected,
                request deletion, and opt out of sale of personal information. I do not sell personal information.
              </p>
            </section>

            {/* 13. CHANGES */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>13. Changes to This Policy</h2>
              <p style={{ fontSize: '1rem', lineHeight: '1.7' }}>
                I may update this Privacy Policy from time to time. The updated version will be indicated
                by an updated "Last Updated" date. I encourage you to review this policy periodically.
              </p>
            </section>

            {/* 14. CONTACT */}
            <section style={sectionStyle}>
              <h2 style={headingStyle}>14. Contact Me</h2>
              <div style={{
                backgroundColor: '#2563eb',
                padding: '30px',
                borderRadius: '10px',
                color: 'white',
                textAlign: 'center'
              }}>
                <p style={{ fontSize: '1.3rem', marginBottom: '15px' }}>📧 shadisayed.6890@gmail.com</p>
                <p style={{ fontSize: '1.2rem', marginBottom: '10px' }}>📞 +201151638804</p>
                <p style={{ fontSize: '1.1rem', marginBottom: '0' }}>📍 Cairo, Egypt</p>
              </div>
              <p style={{ fontSize: '1rem', lineHeight: '1.7', marginTop: '20px', textAlign: 'center', color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                For privacy-related questions, please email me. I'll respond within 24–48 hours.
              </p>
            </section>

            {/* FOOTER */}
            <div style={{
              marginTop: '40px',
              paddingTop: '20px',
              borderTop: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
              textAlign: 'center'
            }}>
              <p style={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '0.9rem' }}>
                © {new Date().getFullYear()} Shadi Sayed Mohamed Ahmed. All Rights Reserved.
              </p>
              <p style={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '0.9rem' }}>
                Version 1.1.0 | Last Updated: {currentDate}
              </p>
            </div>

          </Card.Body>
        </Card>
      </Container>
    </motion.div>
  );
};

export default PrivacyPolicy;
